import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { APP_SETTINGS_TOKEN, AppSettingsService } from './app-settings.types';

export type LoadingPhase = 'stall' | 'active' | null;

/**
 * Service to track active HTTP requests and provide loading state.
 * Used to show a global loading indicator when network calls are in progress.
 * Supports stalling requests before actual network calls to prevent wasteful requests.
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private activeRequests = new Set<string>();
  private stalledRequests = new Set<string>();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingPhaseSubject = new BehaviorSubject<LoadingPhase>(null);
  private appSettings = inject<AppSettingsService>(APP_SETTINGS_TOKEN);
  private settingsSubscription?: Subscription;

  /**
   * Observable that emits true when there are active or stalled requests, false otherwise.
   */
  public isLoading$: Observable<boolean> = this.loadingSubject.asObservable();

  /**
   * Observable that emits the current loading phase: 'stall', 'active', or null.
   */
  public loadingPhase$: Observable<LoadingPhase> = this.loadingPhaseSubject.asObservable();

  constructor() {
    // Subscribe to settings changes to react to stall duration updates
    this.settingsSubscription = this.appSettings.settings$.subscribe(() => {
      // Settings changes don't affect current requests, but new requests will use new duration
    });
  }

  /**
   * Register a new stalled request (before actual network call).
   * @param requestId - Unique identifier for the request (typically URL + method)
   */
  addStalledRequest(requestId: string): void {
    this.stalledRequests.add(requestId);
    this.updateLoadingState();
    this.updateLoadingPhase();
  }

  /**
   * Transition a request from stalled to active (actual network call starting).
   * @param requestId - Unique identifier for the request
   */
  activateRequest(requestId: string): void {
    this.stalledRequests.delete(requestId);
    this.activeRequests.add(requestId);
    this.updateLoadingState();
    this.updateLoadingPhase();
  }

  /**
   * Cancel all stalled requests (e.g., on navigation).
   */
  cancelStalledRequests(): void {
    this.stalledRequests.clear();
    this.updateLoadingState();
    this.updateLoadingPhase();
  }

  /**
   * Register a new active request.
   * @param requestId - Unique identifier for the request (typically URL + method)
   */
  addRequest(requestId: string): void {
    this.activeRequests.add(requestId);
    this.updateLoadingState();
    this.updateLoadingPhase();
  }

  /**
   * Remove a completed request.
   * @param requestId - Unique identifier for the request
   */
  removeRequest(requestId: string): void {
    this.activeRequests.delete(requestId);
    this.stalledRequests.delete(requestId);
    this.updateLoadingState();
    this.updateLoadingPhase();
  }

  /**
   * Update the loading state based on active and stalled requests.
   */
  private updateLoadingState(): void {
    const isLoading = this.activeRequests.size > 0 || this.stalledRequests.size > 0;
    this.loadingSubject.next(isLoading);
  }

  /**
   * Update the loading phase based on current requests.
   * Priority: 'active' > 'stall' > null
   */
  private updateLoadingPhase(): void {
    if (this.activeRequests.size > 0) {
      this.loadingPhaseSubject.next('active');
    } else if (this.stalledRequests.size > 0) {
      this.loadingPhaseSubject.next('stall');
    } else {
      this.loadingPhaseSubject.next(null);
    }
  }

  /**
   * Get the current loading state.
   */
  get isLoading(): boolean {
    return this.activeRequests.size > 0 || this.stalledRequests.size > 0;
  }

  /**
   * Get the number of active requests.
   */
  get activeRequestCount(): number {
    return this.activeRequests.size;
  }

  /**
   * Get the number of stalled requests.
   */
  get stalledRequestCount(): number {
    return this.stalledRequests.size;
  }
}
